import api from './api';

/**
 * Gets the backend base URL (origin) without trailing /api or slashes.
 */
export const getBackendApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let clean = process.env.REACT_APP_API_URL.replace(/\/+$/, '');
    if (!clean.endsWith('/api')) clean = `${clean}/api`;
    return clean;
  }
  if (typeof window !== 'undefined') {
    const proto = window.location.protocol;
    const host = window.location.host;
    return `${proto}//${host}/api`;
  }
  return 'https://bholikoitahari.com/api';
};

/**
 * Normalizes an image URL so it can be rendered reliably in an <img src="..." /> tag.
 * Handles blob URLs, data URIs, absolute HTTP(S) URLs, and relative /uploads paths.
 */
export const getImageUrl = (url) => {
  if (!url) return '';
  if (typeof url !== 'string') {
    if (url instanceof File) {
      return URL.createObjectURL(url);
    }
    if (url.preview) return url.preview;
    if (url.url) return getImageUrl(url.url);
    return '';
  }

  let clean = url.trim();
  if (!clean) return '';

  // Local blob URL or data URI
  if (clean.startsWith('blob:') || clean.startsWith('data:')) {
    return clean;
  }

  // Deduplicate any /api/api/ in URL
  clean = clean.replace(/\/api\/api\//g, '/api/');

  // 1. If url contains localhost or 127.0.0.1 (from development testing), strip domain to extract relative path
  if (clean.includes('localhost') || clean.includes('127.0.0.1')) {
    const match = clean.match(/(\/(?:api\/)?uploads\/.+)/i);
    if (match) {
      clean = match[1];
    } else {
      const parts = clean.split('/');
      clean = parts[parts.length - 1];
    }
  }

  // 2. Upgrade http:// to https:// on secure pages
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && clean.startsWith('http://')) {
    clean = clean.replace('http://', 'https://');
  }

  // 3. Rewrite legacy full URLs missing /api prefix (e.g. https://bholikoitahari.com/uploads/img.jpg => https://bholikoitahari.com/api/uploads/img.jpg)
  if ((clean.startsWith('http://') || clean.startsWith('https://')) && clean.includes('/uploads/') && !clean.includes('/api/uploads/')) {
    clean = clean.replace('/uploads/', '/api/uploads/');
  }

  // 4. Return valid external URLs (like Unsplash, Cloudinary)
  if (clean.startsWith('http://') || clean.startsWith('https://')) {
    return clean;
  }

  // 5. Ensure relative path format
  let cleanPath = clean.startsWith('/') ? clean : `/${clean}`;

  // If path is just a filename (e.g., /img_123.png) or starts with /uploads/
  if (!cleanPath.startsWith('/api/uploads/')) {
    if (cleanPath.startsWith('/uploads/')) {
      cleanPath = `/api${cleanPath}`;
    } else if (cleanPath.startsWith('/api/')) {
      cleanPath = cleanPath.replace('/api/', '/api/uploads/');
    } else {
      cleanPath = `/api/uploads${cleanPath}`;
    }
  }

  const apiUrl = getBackendApiUrl();
  const origin = apiUrl.replace(/\/api$/, '');
  return `${origin}${cleanPath}`;
};

/**
 * Uploads a file to /upload/image if it is a File object or object with a file property.
 * Returns the final server URL string. If already a string URL, returns it unchanged.
 */
export const uploadImageFile = async (fileOrUrl) => {
  if (!fileOrUrl) return '';

  let fileToUpload = null;
  if (fileOrUrl instanceof File) {
    fileToUpload = fileOrUrl;
  } else if (fileOrUrl && typeof fileOrUrl === 'object' && fileOrUrl.file instanceof File) {
    fileToUpload = fileOrUrl.file;
  } else if (typeof fileOrUrl === 'string') {
    return fileOrUrl;
  }

  if (fileToUpload) {
    const formData = new FormData();
    formData.append('image', fileToUpload);
    const res = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const serverUrl = res.data?.url || res.data?.data?.url;
    if (!serverUrl) throw new Error('Image upload failed, no URL returned');
    return serverUrl;
  }

  return typeof fileOrUrl === 'string' ? fileOrUrl : '';
};

/**
 * Uploads an array of image files/URLs (e.g. for photo galleries).
 * Returns array of resolved server URL strings.
 */
export const uploadImageFiles = async (items) => {
  if (!Array.isArray(items)) return [];
  return Promise.all(items.map(item => uploadImageFile(item)));
};
